import type { Scheduling } from '@src/core/buyerApiClient';
import { resolveDefaultTariffFromGroup } from '@src/domains/tariffs/core/resolveDefaultTariffFromGroup';

export type PackageProductDates = {
  [date: string]: {
    availables: Scheduling[];
    unavailables: Scheduling[];
  };
};

export type IntersectPackageAvailableDatesInput = {
  datesByProductId: Record<string, PackageProductDates | undefined>;
  scheduledProductIds: string[];
};

const toDayKey = (date: string): string => date.slice(0, 10);

export function dateHasDefaultTariff(schedulings: Scheduling[] | undefined): boolean {
  return Boolean(schedulings?.some((scheduling) => resolveDefaultTariffFromGroup(scheduling.tariffGroup)));
}

const hasBookableScheduling = (entry: PackageProductDates[string] | undefined): boolean =>
  Boolean(entry?.availables.length) && dateHasDefaultTariff(entry?.availables);

const normalizeProductDates = (dates: PackageProductDates | undefined): PackageProductDates => {
  if (!dates) {
    return {};
  }

  return Object.entries(dates).reduce<PackageProductDates>((acc, [date, entry]) => {
    const day = toDayKey(date);
    const current = acc[day] ?? { availables: [], unavailables: [] };
    acc[day] = {
      availables: [...current.availables, ...(entry?.availables ?? [])],
      unavailables: [...(current.unavailables ?? []), ...(entry?.unavailables ?? [])],
    };
    return acc;
  }, {});
};

export function intersectPackageAvailableDates({
  datesByProductId,
  scheduledProductIds,
}: IntersectPackageAvailableDatesInput): PackageProductDates {
  if (!scheduledProductIds.length) {
    return {};
  }

  const normalizedByProduct = scheduledProductIds.map((productId) =>
    normalizeProductDates(datesByProductId[productId]),
  );
  const [firstDates, ...otherDates] = normalizedByProduct;

  if (!firstDates) {
    return {};
  }

  return Object.entries(firstDates).reduce<PackageProductDates>((acc, [date, entry]) => {
    if (!hasBookableScheduling(entry)) {
      return acc;
    }

    const isCommon = otherDates.every((dates) => hasBookableScheduling(dates[date]));
    if (isCommon) {
      acc[date] = { availables: entry.availables, unavailables: [] };
    }

    return acc;
  }, {});
}
